import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from '@emotion/styled'

import { ReactComponent as ArrowRight } from '@doveconviene/sf-ui/icons/arrow_right.svg'
import readableDistance from '../../../../utils/readableDistance'

import { uxEvent } from '../../../../flux/actions/ux'
import { selectHasStores, selectIsCampaignExpired } from '../../../../flux/selectors/ux'
import { selectLabel } from '../../../../flux/selectors/i18n'
import { selectUserNearestStore } from '../../../../flux/selectors/stores'

import Icon from '../../../ui/Icon'
import LinkToNearestStore from '../../Links/LinkToNearestStore'
import LoadingSkeleton from '../../Generics/LoadingSkeleton'

const StyledRoot = styled.div(({ theme }) => ({
  height: (theme.spaces.default / 2) * 5, // 40px,
  borderBottom: `1px solid ${theme.colors.light_grey}`,
}))

const StyledContainer = styled.div(({ theme }) => ({
  padding: `0 ${theme.spaces.default}px`,
  maxWidth: theme.breakpoints.xlarge,
  margin: '0 auto',
  width: 'auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: '100%',
  [`@media (min-width: ${theme.breakpoints.small})`]: {
    padding: `0 ${theme.spaces.default}px`,
  },
}))

const StyledContent = styled.div(() => ({
  flex: '1 1 auto',
}))

const StyledText = styled.span(({ theme }) => ({
  color: theme.colors.mid_grey,
  fontSize: theme.fontSize * 0.875,
  fontWeight: '700',
}))

const StyledCtaContent = styled.div(() => ({
  flex: '0 0 auto',
}))

const StyledCta = styled.div(({ theme }) => ({
  border: '0',
  cursor: 'pointer',
  display: 'inline-block',
  verticalAlign: 'middle',
  background: theme.colors.white,
}))

const StyledCtaFlex = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
}))

const StyledCtaText = styled.span(({ theme }) => ({
  color: theme.colors.primary,
  fontWeight: '700',
  fontSize: theme.fontSize * 0.875,
}))

const StyledCtaIcon = styled.span(({ theme }) => ({
  color: theme.colors.primary,
  width: '24px',
  height: '24px',
}))

export const CampaignSubHeaderSkelton = ({ hide }) => (
  <>
    <LoadingSkeleton transparent={hide} height="20px" width="100px" />
    <LoadingSkeleton transparent={hide} height="20px" width="70px" />
  </>
)

CampaignSubHeaderSkelton.propTypes = {
  hide: PropTypes.bool,
}

CampaignSubHeaderSkelton.defaultProps = {
  hide: false,
}

const CampaignSubHeader = ({
  title,
  cta,
  onCTAClick,
}) => {
  const hideLoadingSkeletons = [cta, title].filter(x => !!x)

  return ((cta !== null && title !== null) ? (
    <StyledRoot data-test="CampaignSubHeader">
      <StyledContainer>
        {
          (cta && title) ? (
            <>
              <StyledContent>
                <StyledText data-test="CampaignSubHeaderTitle">
                  { title }
                </StyledText>
              </StyledContent>
              <StyledCtaContent onClick={onCTAClick} data-test="CampaignSubHeaderCta">
                <LinkToNearestStore>
                  <StyledCta>
                    <StyledCtaFlex>
                      <StyledCtaText data-test="CampaignSubHeaderCtaCopy">
                        { cta }
                      </StyledCtaText>
                      <StyledCtaIcon data-test="CampaignSubHeaderCtaIcon">
                        <Icon>
                          <ArrowRight />
                        </Icon>
                      </StyledCtaIcon>
                    </StyledCtaFlex>
                  </StyledCta>
                </LinkToNearestStore>
              </StyledCtaContent>
            </>
          ) : <CampaignSubHeaderSkelton hide={!!hideLoadingSkeletons.length} />
        }
      </StyledContainer>
    </StyledRoot>
  ) : null)
}

CampaignSubHeader.propTypes = {
  title: PropTypes.string,
  cta: PropTypes.string,
  onCTAClick: PropTypes.func.isRequired,
}

CampaignSubHeader.defaultProps = {
  title: undefined,
  cta: undefined,
}

const mapDispatchToProps = {
  uxEvent,
}

const mapStateToProps = state => {
  const isCampaignExpired = selectIsCampaignExpired(state)
  const hasStores = selectHasStores(state)
  const store = selectUserNearestStore(state)
  const nearestStoreAt = selectLabel('nearestStoreAt')(state)
  const ctaCallNum = selectLabel('ctaCallNum')(state)
  const ctaCall = selectLabel('ctaCall')(state)
  const show = selectLabel('show')(state)

  let result = { title: undefined, cta: undefined }

  if (typeof isCampaignExpired === 'boolean') {
    if (hasStores && (store !== undefined && store !== null)) {
      result = {
        title: store?.distance ? `${nearestStoreAt} ${readableDistance(store.distance)}` : null,
        cta: show,
      }
    } else if (hasStores === false && ctaCall && ctaCallNum) {
      result = {
        title: ctaCallNum,
        cta: ctaCall,
      }
    } else {
      result = { title: null, cta: null }
    }
  }

  return { ...result, hasStores }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onCTAClick: () => {
    dispatchProps.uxEvent({
      name: 'click',
      target: 'SubHeader/CTA',
    })
  },
}
)

CampaignSubHeader.displayName = 'CampaignSubHeader'
CampaignSubHeader.whyDidYouRender = true

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(CampaignSubHeader)
